import { startTransition } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import CheckboxField from '@rewardopl/react-ui/checkbox_field';

import { patch, post } from '@rewardopl/utils/network';

import {
  ButtonWrapper,
  CheckboxWrapper,
  ContentWrapper,
  Footer,
  Heading,
  ScrollContainer,
  Wrapper,
} from './index.styles';

import MarketingDisclaimers from '../../../../../common/marketing_disclaimers';
import PrivacyPolicyLink from '../../../../../privacy_policy_link';
import TermsAndConditionsLink from '../../../../../terms_and_conditions_link';
import BirthdayField from '../../../../register/birthday_field';
import UncontrolledSelectField from '../../../../register/uncontrolled_select_field';

import { currentUserState, vendorPlacesQuery } from '../../../../../../store';

import type { PlaceWithDynamicPropertiesForUser, User } from '@rewardopl/types';

export default function AppConsentsPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useAtom(currentUserState);
  const [places, setPlaces] = useAtom(
    vendorPlacesQuery({
      includePlacesWithoutLocation: true,
    }),
  );
  const acceptTermsErrorString = useTranslation('Failed to accept Terms and Conditions');

  const { terms_accepted: termsAccepted } = currentUser;

  if (termsAccepted) {
    return <Navigate to=".." />;
  }

  const action = '/api/users/current';

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const form = event.currentTarget;

    if (!form || !form.reportValidity()) {
      return;
    }

    const formData = new FormData(form);
    const filteredFormData = new FormData();

    for (const [key, value] of formData.entries()) {
      if (key !== 'place_id' && value !== '') {
        filteredFormData.append(key, value);
      }
    }

    const placeId = formData.get('place_id') as PlaceWithDynamicPropertiesForUser['_id'] | null;

    const placesToLike = [];
    if (placeId) {
      placesToLike.push(placeId);
    }

    try {
      const userPromise = (await patch(action, {
        body: filteredFormData,
      })) as User;

      const likePromises = placesToLike.map(
        (placeId) =>
          post(`/api/places/${placeId}/like`) as Promise<PlaceWithDynamicPropertiesForUser>,
      );

      const [user, ...updatedPlaces] = await Promise.all([userPromise, ...likePromises]);

      startTransition(() => {
        setPlaces((prevPlaces) =>
          prevPlaces.map(
            (currentPlace) =>
              updatedPlaces.find((place) => place._id === currentPlace._id) || currentPlace,
          ),
        );

        setCurrentUser(user);

        navigate('../');
      });
    } catch {
      toast.error(acceptTermsErrorString);
    }
  }

  return (
    <form action={action} method="PATCH" onSubmit={onSubmit}>
      <Wrapper>
        <ScrollContainer>
          <ContentWrapper>
            <Heading>
              <T>Terms and Conditions</T>
            </Heading>
            <CheckboxWrapper>
              <UncontrolledSelectField
                label="Gender"
                name="gender"
                options={[
                  { value: 'male', label: 'Male' },
                  { value: 'female', label: 'Female' },
                  { value: 'non-binary', label: 'Non-binary' },
                ]}
                required={false}
              />
              <BirthdayField label="Birthday" name="birthday" required={false} />
              <UncontrolledSelectField
                label="Favorite Lajkonik venue"
                name="place_id"
                options={places.map((place) => ({
                  value: place._id,
                  label: place.name,
                }))}
                required={false}
              />
              <CheckboxField
                label={
                  <>
                    <T>I have read and agree to the</T> <TermsAndConditionsLink /> <T>and</T>{' '}
                    <PrivacyPolicyLink />.
                  </>
                }
                name="terms_accepted"
                required
              />
            </CheckboxWrapper>
            <MarketingDisclaimers />
          </ContentWrapper>
        </ScrollContainer>
        <Footer>
          <ButtonWrapper>
            <Button className="primary" type="submit">
              <T>Continue</T>
            </Button>
          </ButtonWrapper>
        </Footer>
      </Wrapper>
    </form>
  );
}
